
import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Loader2, Facebook, Linkedin } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

const Login = () => {
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isFacebookLoading, setIsFacebookLoading] = useState(false);
  const [isLinkedInLoading, setIsLinkedInLoading] = useState(false);
  const [error, setError] = useState("");
  const [facebookError, setFacebookError] = useState("");
  const [linkedinError, setLinkedinError] = useState("");
  const { signInWithGoogle, signInWithFacebook, signInWithLinkedIn, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    if (user) {
      const redirectUrl = localStorage.getItem('authRedirectUrl');
      if (redirectUrl) {
        localStorage.removeItem('authRedirectUrl');
        navigate(redirectUrl);
      } else {
        navigate('/');
      }
    }
  }, [user, navigate]);

  const handleGoogleSignIn = async () => {
    try {
      setIsGoogleLoading(true);
      setError("");
      setFacebookError("");
      setLinkedinError("");
      await signInWithGoogle();
    } catch (error: any) {
      console.error("Google sign in error:", error);
      setError(error.message || "Failed to sign in with Google. Please try again.");
      setIsGoogleLoading(false);
    }
  };

  const handleFacebookSignIn = async () => {
    try {
      setIsFacebookLoading(true);
      setError("");
      setFacebookError("");
      setLinkedinError("");
      await signInWithFacebook();
    } catch (error: any) {
      console.error("Facebook sign in error:", error);
      
      if (error.message?.includes("provider is not enabled")) {
        setFacebookError("Facebook login is not properly configured. The app administrator needs to complete the Facebook app setup.");
      } else {
        setError(error.message || "Failed to sign in with Facebook. Please try again.");
      }
      
      setIsFacebookLoading(false);
    }
  };

  const handleLinkedInSignIn = async () => {
    try {
      setIsLinkedInLoading(true);
      setError("");
      setFacebookError("");
      setLinkedinError("");
      await signInWithLinkedIn();
    } catch (error: any) {
      console.error("LinkedIn sign in error:", error);
      
      if (error.message?.includes("provider is not enabled")) {
        setLinkedinError("LinkedIn login is not properly configured. The app administrator needs to complete the LinkedIn app setup.");
      } else {
        setError(error.message || "Failed to sign in with LinkedIn. Please try again.");
      }
      
      setIsLinkedInLoading(false);
    }
  };

  return (
    <Layout title="Login" path="/login">
      <div className="min-h-[80vh] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <Card className="w-full max-w-md shadow-xl border">
          <CardHeader className="pb-2">
            <CardTitle className="text-3xl font-bold text-center">
              <span className="text-[#34BF49]">Welcome Back</span>
            </CardTitle>
            <CardDescription className="text-center text-gray-600 mt-4">
              Log in to your Freelance With Andy account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-4">
            {error && (
              <Alert variant="destructive" className="border border-red-200">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {facebookError && (
              <Alert variant="destructive">
                <AlertDescription>
                  {facebookError}
                  <p className="mt-2 text-sm">
                    <strong>Note for developers:</strong> You need to add your app's domain to the Facebook app configuration 
                    in the Facebook Developer Console.
                  </p>
                </AlertDescription>
              </Alert>
            )}

            {linkedinError && (
              <Alert variant="destructive">
                <AlertDescription>
                  {linkedinError}
                  <p className="mt-2 text-sm">
                    <strong>Note for developers:</strong> You need to enable and configure the LinkedIn provider 
                    in your Supabase Authentication settings.
                  </p>
                </AlertDescription>
              </Alert>
            )}
            
            <div className="grid grid-cols-1 gap-4">
              <Button
                type="button"
                className="w-full h-12 flex items-center justify-center gap-3 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 shadow-sm transition-all"
                onClick={handleGoogleSignIn}
                disabled={isGoogleLoading}
              >
                {isGoogleLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                )}
                <span className="font-medium">Continue with Google</span>
              </Button>
              
              <Button
                type="button"
                className="w-full h-12 flex items-center justify-center gap-3 bg-[#1877F2] hover:bg-[#166FE5] text-white transition-all"
                onClick={handleFacebookSignIn}
                disabled={isFacebookLoading}
              >
                {isFacebookLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Facebook className="h-5 w-5" />
                )}
                <span className="font-medium">Continue with Facebook</span>
              </Button>

              <Button
                type="button"
                className="w-full h-12 flex items-center justify-center gap-3 bg-[#0A66C2] hover:bg-[#004182] text-white transition-all"
                onClick={handleLinkedInSignIn}
                disabled={isLinkedInLoading}
              >
                {isLinkedInLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Linkedin className="h-5 w-5" />
                )}
                <span className="font-medium">Continue with LinkedIn</span>
              </Button>
            </div>
          </CardContent>
          <CardFooter className="flex justify-center pb-8">
            <p className="text-sm text-center text-gray-600">
              Don't have an account?{" "}
              <Link to="/signup" className="text-green-600 hover:text-green-700 font-medium hover:underline transition-colors">
                Sign up
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </Layout>
  );
};

export default Login;
